<template>
  <div class="fulfillment-cue-main">
    <div v-if="windowFails && !isLoading" class="fulfillment-col">
      <Spinner></Spinner>
      <div v-if="windowFails" class="waiting-text">
        El bloqueo de ventanas emergentes de tu dispositivo está activado, si deseas continuar tu validación de
        identidad has click en el botón.
      </div>
      <PdsButton
        label="Continuar"
        :href="urlCode"
        onclick="window.open(this.getAttribute('href'))"
        @click="startPolling(transactionId)"
      />
    </div>
    <div v-if="isLoading && !windowFails">
      <div class="waiting-text">Estamos esperando a que la validación esté completa.</div>
      <Spinner></Spinner>
    </div>
    <div v-else-if="processFinished" class="container-message">
      <div class="img-result">
        <img :src="urlResultImage" :alt="imageAlt" />
      </div>
      <div>
        <div class="first-text">
          {{ descriptionText }}
        </div>
        <div class="text-info">
          {{ detailText }}
        </div>
      </div>
    </div>
    <div v-else-if="!windowFails" class="fulfillment-footer-section">
      <div class="fulfillment-col">
        <PdsButton label="Iniciar Proceso" @click="startCUE()" />
      </div>
    </div>
  </div>
</template>

<script>
import PdsButton from "@vue-pds/button";
import Spinner from "@/components/facialRecognition/Spinner";
import Config from "@/utils/config";
import { FFResultEvent } from "fulfillment-commons-js";
import * as web from "@/utils/web";

export default {
  name: "UniqueStatePassword",
  components: {
    Spinner,
    PdsButton,
  },
  props: {
    company: { type: String, default: "principal" },
    rut: { type: String, required: true },
    dummyMode: { type: Boolean, default: false },
    cueService: { type: Object, required: true },
    channelKey: { type: String },
    portalId: { type: String },
    successCallback: { type: Function },
    failureCallback: { type: Function },
    autostart: { type: Boolean, default: false },
  },
  data() {
    return {
      isLoading: false,
      processFinished: false,
      error: false,
      urlCode: "",
      detailText: "Ocurrió un error en el sistema",
      urlResultImage: "",
      imageAlt: "Error Image",
      descriptionText: "Algo salió mal",
      pollingIntervalInMillis: process.env.VUE_APP_CUE_POLLING_INTERVAL,
      config: undefined,
      windowFails: false,
      transactionId: undefined,
    };
  },
  created() {
    this.config = new Config(this.environment);
    if (this.autostart) this.startCUE();
  },
  methods: {
    nextStepSuccess(result) {
      this.isLoading = false;
      this.processFinished = true;
      this.urlResultImage = this.config.successImage();
      this.imageAlt = "Success Image";
      this.descriptionText = "¡Muy bien!";
      this.detailText = "¡Hemos validado con éxito tu identidad con clave única del estado!";
      this.$emit("result", FFResultEvent.success(result, this.traceId));
      if (typeof this.successCallback === "function") {
        this.successCallback(result);
      }
    },
    nextStepError(error) {
      this.isLoading = false;
      this.processFinished = true;
      this.urlResultImage = this.config.errorImage();
      this.imageAlt = "Error Image";
      this.descriptionText = "Algo salió mal";
      this.detailText = "Ocurrió un error en la validación";
      if (error) {
        this.$emit("result", FFResultEvent.failure(error, this.traceId));
        if (typeof this.failureCallback === "function") {
          this.failureCallback(error);
        }
      }
    },
    pollStatus(transactionId, startTime) {
      let timePassed = Date.now() - startTime;
      if (timePassed < process.env.VUE_APP_CUE_POLLING_MAX_TIME) {
        setTimeout(() => {
          this.cueService
            .getStatus(transactionId, this.company)
            .then((response) => {
              if (response.status === "CREATED" || response.status === "PENDING") {
                this.pollStatus(transactionId, startTime);
              } else if (response.status === "APPROVED") {
                this.nextStepSuccess(response);
              } else if (response.status === "REJECTED") {
                this.nextStepError({
                  reason: "REJECTED",
                  message: response.error.detail,
                  source: response.error.source,
                });
              } else if (response.status === "EXPIRED") {
                this.nextStepError({
                  reason: response.error.code,
                  message: response.error.detail,
                  source: response.error.source,
                });
              } else if (response.status === "ERROR") {
                if (response.error) {
                  this.nextStepError({
                    reason: response.error.code,
                    message: response.error.detail,
                    source: response.error.source,
                  });
                } else {
                  this.nextStepError({
                    reason: "UNKNOWN_ERROR",
                    message: `Unknown error`,
                    source: "auth-widget",
                  });
                }
              }
            })
            .catch(() => {
              this.nextStepError({
                reason: "COMMUNICATION_ERROR",
                message: `Failed to obtain Unique State Password info`,
                source: "auth-widget",
              });
            });
        }, this.pollingIntervalInMillis);
      } else {
        this.cueService.markAsExpired(transactionId, this.company, this.portalId);
        this.nextStepError({
          reason: "POLLING_EXPIRED",
          message: `Limit time for polling reached`,
          source: "auth-widget",
        });
      }
    },
    startCUE() {
      this.isLoading = true;
      this.cueService
        .getAccessToken(
          {
            rut: this.rut,
            company: this.company,
            portalId: this.portalId,
          },
          this.channelKey
        )
        .then((response) => {
          if (this.dummyMode) {
            this.cueService.getStatus(this.transactionId, this.company).then((response) => {
              this.isLoading = false;
              response.status = "APPROVED";
              this.nextStepSuccess(response);
            });
          } else if (response.status === "CREATED" || response.status === "PENDING") {
            this.urlCode = process.env.VUE_APP_CUE_START_VALIDATION;
            this.urlCode = `${this.urlCode}?access_token=${response.providerData.sessionToken}`;
            const urlFinal = web.sanitizeUrl(this.urlCode);
            if(!urlFinal) {
              this.nextStepError({
                reason: "VALIDATION_ERROR",
                message: `The url provided is not secure`,
                source: "auth-widget",
              }) 
            } else {
              this.transactionId = response.transactionId;
              const newWin = window.open(urlFinal);
              if (!newWin || newWin.closed || typeof newWin.closed === "undefined") {
                this.isLoading = false;
                this.windowFails = true;
              } else {
                this.startPolling(this.transactionId);
              }
            }
          }
        })
        .catch(() => {
          this.nextStepError({
            reason: "COMMUNICATION_ERROR",
            message: `Failed to obtain access token`,
            source: "auth-widget",
          });
        });
    },
    startPolling(transactionId) {
      this.isLoading = true;
      this.windowFails = false;
      this.pollStatus(transactionId, Date.now());
    },
  },
  computed: {
    fieldsCompleted() {
      return this.rut !== "";
    },
  },
};
</script>
<style scoped src="@pds/core/core.min.css"></style>
<style scoped src="@pds/typography/typography.min.css"></style>
<style scoped>
.container-message {
  margin: auto;
  width: 300px;
}

.img-result {
  padding: 15px 0 0;
  text-align: center;
}

.first-text {
  font-size: 22px;
  line-height: 26px;
  letter-spacing: 0px;
  text-align: center;
  margin-top: 15px;
}

.text-info {
  color: #464646;
  font-size: 16px;
  line-height: 22px;
  padding: 15px 0;
  text-align: center;
}

.waiting-text {
  width: 80%;
  padding: 5%;
  text-align: center;
  margin: auto;
}
.fulfillment-cue-main {
  color: #4a4a4a;
  padding: 15px;
  max-width: 600px;
  margin: auto;
  font-family: "FS Elliot Web Regular", sans-serif;
}

.fulfillment-col {
  display: table-cell;
  vertical-align: middle;
  text-align: center;
}

.fulfillment-footer-section {
  display: table;
  width: 100%;
  margin-top: 27px;
}
</style>
